import { useMemo } from 'react';

import { SlLEContext } from '../../global-constants';
import { SlDataService } from '../../services/sl-data.service';
import { QuizFeedbackType } from '../../global-interface';
import { SolutionContext } from '../../../../kodie/private/global-constants';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import {
	IMaterialBody, UIComponentType, IQuestionPart, QuestionPartType, MaterialType, IMaterialInfo, AnswerType,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

export function useKodieData(isAnswerExplanation: boolean) {
	const {
		lesson$: lesson,
		course$: course,
		lessonPageIndex$: lessonPageIndex,
		question$: question,
		answerOptions$: answerOptions,
		quizFeedback$: quizFeedback,
	} = useContainerData(SlLEContext, SlDataService, [
		'lesson$',
		'course$',
		'lessonPageIndex$',
		'question$',
		'quizFeedback$',
		'answerOptions$',
	]);

	const getHtmlFromComponent = (component: IMaterialBody) => (component?.componentTypeId === UIComponentType.CodeSnippet
		? `\`\`\`${component.content.language}\n${component?.content?.data}\`\`\``
		: component?.content?.data);
	const getTextFromHtml = (html: string) => {
		const parser = new DOMParser();
		const htmlDoc = parser.parseFromString(html, 'text/html');
		return htmlDoc.documentElement.textContent || '';
	};
	const getContentFromQuestionInputPart = (parts: IQuestionPart[]) => (
		parts
			?.map((part) => {
				if (part.type === QuestionPartType.NewLine) {
					return '\n';
				}

				if (part.type === QuestionPartType.Text) {
					return part.content;
				}

				return '{BLANKS}';
			})
			.join('') || ''
	);
	const theory = useMemo(() => {
		if (!lesson || lessonPageIndex === null) return '';

		let htmlString = '';
		const currLessonPage = lesson.children[lessonPageIndex - 1];
		currLessonPage.children.forEach((material) => {
			if (material.materialInfo.header.typeId === MaterialType.Theory) {
				htmlString += material.materialInfo.body
					.map((component) => getHtmlFromComponent(component))
					.join('');
				htmlString += '\n';
			}
		});

		return getTextFromHtml(htmlString);
	}, [lesson, lessonPageIndex]);

	const getQuestionContent = (
		questionData: IMaterialInfo,
		includeTextFromInput = true,
	) => questionData?.body
		.filter(
			(item) => (includeTextFromInput
				&& [
					UIComponentType.MultiTypeIn,
					UIComponentType.TypeIn,
					UIComponentType.DragAndDrop,
				].includes(item.componentTypeId))
				|| item?.content?.data,
		)
		.map((item) => {
			if (
				[
					UIComponentType.MultiTypeIn,
					UIComponentType.TypeIn,
					UIComponentType.DragAndDrop,
				].includes(item.componentTypeId)
			) {
				return getContentFromQuestionInputPart(item.content.parts);
			}

			const html = getHtmlFromComponent(item) || '';

			if (item.componentTypeId === UIComponentType.CodeSnippet) {
				return html;
			}

			return getTextFromHtml(html);
		});

	const getAnswer = (questionData: IMaterialInfo) => {
		const materialWithInputType = questionData?.body.find((item) => [
			UIComponentType.MultiTypeIn,
			UIComponentType.TypeIn,
			UIComponentType.DragAndDrop,
		].includes(item.componentTypeId));

		if (!materialWithInputType) {
			return answerOptions
				.filter(
					(answer) => typeof answer.isCorrect !== 'undefined'
						&& answer.isCorrect !== null,
				)
				.map((answer) => answer.text)
				.join('\n');
		}

		const inputContent = getContentFromQuestionInputPart(
			materialWithInputType.content.parts,
		);

		let index = 0;
		return inputContent.replace(/{BLANKS}/g, () => answerOptions
			.filter(
				(answer) => typeof answer.isCorrect !== 'undefined'
					&& answer.isCorrect !== null,
			)
			.sort((a, b) => a.orderNumber - b.orderNumber)?.[index++]?.text);
	};

	const questionData = useMemo(() => {
		if (!question) {
			return null;
		}

		const res = {
			questionType: AnswerType[question?.answer.answerTypeId],
			options: question?.answer.options?.map((answer) => answer.text),
			content: getQuestionContent(question),
		};

		return res;
	}, [question]);

	const answerExplanationData = useMemo(() => {
		if (!question) {
			return null;
		}

		const res = {
			question: getQuestionContent(question, false),
			answer: getAnswer(question),
			dynamicId: question.header.dynamicId,
			answerOptions: answerOptions.filter(
				a => typeof a.isCorrect !== 'undefined'
					&& a.isCorrect !== null
			),
			isCorrect: [
				QuizFeedbackType.Correct,
				QuizFeedbackType.Correct3InARow,
				QuizFeedbackType.Correct5InARow,
			].includes(quizFeedback),
		};

		return res;
	}, [question, quizFeedback, answerOptions]);

	const solutionsData = useMemo(
		() => ({
			materialType: MaterialType.LessonPage,
			materialHeader: lesson?.materialInfo.header,
			courseName: course?.materialInfo.header.title,
			lessonContent: theory,
			questionData,
			answerExplanationData,
			solutionContext: isAnswerExplanation
				? SolutionContext.Answer
				: SolutionContext.Lesson,
		}),
		[lesson, course, theory, questionData, answerExplanationData],
	);

	return solutionsData;
}
